// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useEffect, useRef } from 'react';
import { Alert, Dropdown, FormInput, InputLabel, Radio } from '@loa/ui-components';
import type {
  MaritalStatusInput,
  MaxFundingScenarioInputs,
  SelectedPension,
} from '../../../../types/calculationResult';
import { retirementAgeOptions, sftForScenario } from './maxFundingDefaults';
import { EuroFormInput } from './EuroFormInput';
import { PensionPolicyPicker } from './PensionPolicyPicker';

type MaxFundingScenarioFormProps = Readonly<{
  inputs: MaxFundingScenarioInputs;
  onChange: (next: MaxFundingScenarioInputs) => void;
  profileId: string;
  clientId: string;
  selectedPensions: SelectedPension[];
  onSelectedPensionsChange: (next: SelectedPension[]) => void;
  disabled?: boolean;
}>;

/** Stable identity hash of the selected-pension list — when this changes,
 *  the auto-totals effect should re-derive Previous/Current values. */
function computeSelectionSnapshot(selections: SelectedPension[]): string {
  return selections
    .map(
      (p) => `${p.policyId}:${p.amount ?? ''}:${p.subType ?? ''}:${p.leftService ? '1' : '0'}:${p.isActive ? '1' : '0'}`,
    )
    .join('|');
}

/** Pure aggregator: split DB/DC amounts across Previous/Current totals
 *  per the rules described above the call-site. */
function computeTotalsFromSelections(selections: SelectedPension[]): {
  defBenPrevTotal: number;
  defConPrevTotal: number;
  defConCurTotal: number;
} {
  let defBenPrevTotal = 0;
  let defConPrevTotal = 0;
  let defConCurTotal = 0;
  for (const p of selections) {
    if (p.amount == null || !Number.isFinite(p.amount)) continue;
    if (p.subType !== 'DC') {
      defBenPrevTotal += p.amount;
      continue;
    }
    if (!p.isActive) continue;
    if (p.leftService) defConPrevTotal += p.amount;
    else defConCurTotal += p.amount;
  }
  return { defBenPrevTotal, defConPrevTotal, defConCurTotal };
}

const MARITAL_OPTIONS: Array<{ value: MaritalStatusInput; text: string }> = [
  { value: 'SINGLE', text: 'Single' },
  { value: 'MARRIED', text: 'Married' },
  { value: 'CIVIL_PARTNERSHIP', text: 'Civil partnership' },
  { value: 'DIVORCED', text: 'Divorced' },
  { value: 'WIDOWED', text: 'Widowed' },
  { value: 'SEPARATED', text: 'Separated' },
];

export function MaxFundingScenarioForm({
  inputs,
  onChange,
  profileId,
  clientId,
  selectedPensions,
  onSelectedPensionsChange,
  disabled,
}: MaxFundingScenarioFormProps) {
  const patch = (update: Partial<MaxFundingScenarioInputs>) => {
    onChange({ ...inputs, ...update });
  };

  const sftCap = sftForScenario(inputs.dateOfBirth, inputs.retirementAge);

  // Auto-populate totals from the selected pensions. Snapshot-ref guard: on
  // first render we record the selection and don't patch (so loading a saved
  // calculation keeps its persisted totals). Once the user interacts with the
  // picker, any further change recomputes the buckets from the selection.
  //
  // Rules:
  //  - DB: legacy behavior — sum of all selected DBs feeds Previous. Cur stays
  //    user-editable.
  //  - DC: only ACTIVE policies count. Policies with a "Date of leaving
  //    service" filled in feed Previous; policies without it feed Current.
  const prevSelectionSnapshotRef = useRef<string | null>(null);
  useEffect(() => {
    if (!inputs.existingPension) return;
    const snapshot = computeSelectionSnapshot(selectedPensions);
    if (prevSelectionSnapshotRef.current === null) {
      prevSelectionSnapshotRef.current = snapshot;
      return;
    }
    if (prevSelectionSnapshotRef.current === snapshot) return;
    prevSelectionSnapshotRef.current = snapshot;
    patch(computeTotalsFromSelections(selectedPensions));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPensions, inputs.existingPension]);

  return (
    <div
      className="flex flex-col gap-md p-lg bg-white rounded-card shadow-[0_0_10px_0_rgba(0,0,0,0.10)]"
      data-testid="max-funding-form"
    >
      {!inputs.dateOfBirth && (
        <Alert variant="warning" header="Date of birth is missing" data-testid="dob-missing-warning">
          <p className="m-0">
            The client record has no date of birth. Please fill it in on the client profile before
            running the calculation — otherwise the maximum funding calculation will fail.
          </p>
        </Alert>
      )}

      <div className="flex flex-col gap-sm">
        <Dropdown
          label="Normal Retirement Age (NRA)"
          required
          options={retirementAgeOptions()}
          value={String(inputs.retirementAge)}
          onChange={(value) => patch({ retirementAge: Number(value) })}
          disabled={disabled}
          data-testid="field-retirement-age"
        />
      </div>

      <div className="grid grid-cols-2 gap-md items-end">
        <div className="flex flex-col gap-sm">
          <InputLabel required>Current Gross PAYE Earnings</InputLabel>
          <EuroFormInput
            placeholder="0.00"
            value={inputs.currentGrossPayeEarnings || ''}
            onChange={(e) => patch({ currentGrossPayeEarnings: Number(e.target.value) })}
            disabled={disabled}
            max={sftCap}
            data-testid="field-gross-paye"
          />
        </div>
        <div className="flex flex-col gap-sm">
          <InputLabel required>Years With Current Employer To Date</InputLabel>
          <FormInput
            type="number"
            value={inputs.yearsWithCurrentEmployerToDate || ''}
            onChange={(e) =>
              patch({ yearsWithCurrentEmployerToDate: Number(e.target.value) })
            }
            disabled={disabled}
            min={0}
            max={40}
            step="1"
            data-testid="field-years-employer"
          />
        </div>
      </div>

      <div className="flex flex-col gap-sm">
        <Dropdown
          label="Marital status"
          required
          options={MARITAL_OPTIONS.map((o) => ({ value: o.value, text: o.text }))}
          value={inputs.maritalStatus}
          onChange={(value) => patch({ maritalStatus: value as MaritalStatusInput })}
          disabled={disabled}
          data-testid="field-marital"
        />
      </div>

      <hr className="border-0 border-t border-[#E5E5EA] my-sm" />

      <div className="flex flex-col gap-sm">
        <InputLabel required>Does the client have an existing pension?</InputLabel>
        <div className="flex flex-col gap-lg">
          <Radio
            name="existing-pension"
            value="YES"
            checked={inputs.existingPension === true}
            onChange={() => patch({ existingPension: true })}
            disabled={disabled}
            label="Yes"
            data-testid="field-existing-yes"
          />
          <Radio
            name="existing-pension"
            value="NO"
            checked={inputs.existingPension === false}
            onChange={() => {
              patch({
                existingPension: false,
                defBenPrevTotal: 0,
                defConPrevTotal: 0,
                defBenCurTotal: 0,
                defConCurTotal: 0,
              });
              onSelectedPensionsChange([]);
            }}
            disabled={disabled}
            label="No"
            data-testid="field-existing-no"
          />
        </div>
      </div>

      {inputs.existingPension && (
        <>
          <PensionPolicyPicker
            profileId={profileId}
            clientId={clientId}
            selectedPensions={selectedPensions}
            onChange={onSelectedPensionsChange}
            disabled={disabled}
          />

          <div className="grid grid-cols-2 gap-md">
            <div className="flex flex-col gap-sm">
              <InputLabel>Total Defined Benefit Previous</InputLabel>
              <EuroFormInput
                value={inputs.defBenPrevTotal ?? 0}
                onChange={(e) => patch({ defBenPrevTotal: Number(e.target.value) })}
                disabled={disabled}
                max={sftCap}
                data-testid="field-defben-prev"
              />
            </div>
            <div className="flex flex-col gap-sm">
              <InputLabel>Total Defined Contribution Previous</InputLabel>
              <EuroFormInput
                value={inputs.defConPrevTotal ?? 0}
                onChange={(e) => patch({ defConPrevTotal: Number(e.target.value) })}
                disabled={disabled}
                max={sftCap}
                data-testid="field-defcon-prev"
              />
            </div>
            <div className="flex flex-col gap-sm">
              <InputLabel>Total Defined Benefit Current</InputLabel>
              <EuroFormInput
                value={inputs.defBenCurTotal ?? 0}
                onChange={(e) => patch({ defBenCurTotal: Number(e.target.value) })}
                disabled={disabled}
                max={sftCap}
                data-testid="field-defben-cur"
              />
            </div>
            <div className="flex flex-col gap-sm">
              <InputLabel>Total Defined Contribution Current</InputLabel>
              <EuroFormInput
                value={inputs.defConCurTotal ?? 0}
                onChange={(e) => patch({ defConCurTotal: Number(e.target.value) })}
                disabled={disabled}
                max={sftCap}
                data-testid="field-defcon-cur"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
